import { Directive, ElementRef, HostListener, Optional, Self } from '@angular/core';
import { BsDatepickerDirective, BsLocaleService } from 'ngx-bootstrap/datepicker';
import { formatDate, isDateValid, parseDate } from 'ngx-bootstrap/chronos';

const NAVIGATION_KEYS = new Set([
    'Backspace', 'Delete', 'Tab', 'Escape', 'Enter',
    'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
    'Home', 'End'
]);

/**
 * Keeps manual entry on ngx-bootstrap date inputs limited to the active date format.
 * Letters and other characters that cannot appear in that format are rejected immediately.
 * A value that is not a real date is cleared so the form cannot submit it.
 */
@Directive({
    selector: 'input[bsDatepicker]',
    standalone: false
})
export class StrictDateInputDirective {
    constructor(
        private readonly elementRef: ElementRef<HTMLInputElement>,
        @Optional() @Self() private readonly datepicker: BsDatepickerDirective,
        @Optional() private readonly localeService: BsLocaleService
    ) { }

    @HostListener('keydown', ['$event'])
    onKeydown(event: KeyboardEvent): void {
        if (this.isLocked || event.ctrlKey || event.metaKey || event.altKey || NAVIGATION_KEYS.has(event.key)) {
            return;
        }
        if (event.key.length === 1 && !this.isAllowedChar(event.key)) {
            event.preventDefault();
        }
    }

    @HostListener('beforeinput', ['$event'])
    onBeforeInput(event: InputEvent): void {
        if (this.isLocked || event.data == null) {
            return;
        }
        if ([...event.data].some(char => !this.isAllowedChar(char))) {
            event.preventDefault();
        }
    }

    @HostListener('paste', ['$event'])
    onPaste(event: ClipboardEvent): void {
        if (this.isLocked) {
            return;
        }
        const text = event.clipboardData?.getData('text') ?? '';
        if ([...text].every(char => this.isAllowedChar(char))) {
            return;
        }
        event.preventDefault();
        this.insertText(this.sanitize(text));
    }

    @HostListener('input')
    onInput(): void {
        if (this.isLocked) {
            return;
        }
        const input = this.elementRef.nativeElement;
        const sanitized = this.sanitize(input.value);
        if (sanitized !== input.value) {
            input.value = sanitized;
        }
    }

    @HostListener('change')
    @HostListener('blur')
    commitDisplayedValue(): void {
        if (this.isLocked) {
            return;
        }
        const input = this.elementRef.nativeElement;
        const raw = (input.value ?? '').trim();
        if (!raw || this.isDisplayedValueValid(raw)) {
            return;
        }
        input.value = '';
        if (this.datepicker) {
            this.datepicker.bsValue = undefined;
        }
    }

    private get isLocked(): boolean {
        const input = this.elementRef.nativeElement;
        return input.readOnly || input.disabled;
    }

    private get dateInputFormat(): string {
        return this.datepicker?._config?.dateInputFormat || this.datepicker?.bsConfig?.dateInputFormat || 'L';
    }

    private get locale(): string {
        return this.localeService?.currentLocale || 'en';
    }

    private isAllowedChar(char: string): boolean {
        if (/[0-9]/.test(char) || char === '/' || char === '-' || char === '.') {
            return true;
        }
        if (!this.allowsNamedParts) {
            return false;
        }
        return /[a-zA-Z\s,]/.test(char);
    }

    /** Month and weekday names are allowed only when the active format spells them out. */
    private get allowsNamedParts(): boolean {
        const format = this.dateInputFormat;
        return /M{3,}|d{3,}|Do|\bL{2,}\b|\bl{2,}\b/.test(format);
    }

    private sanitize(value: string): string {
        return [...value].filter(char => this.isAllowedChar(char)).join('');
    }

    private isDisplayedValueValid(raw: string): boolean {
        const format = this.dateInputFormat;
        const locale = this.locale;
        const current = this.datepicker?._bsValue;
        if (current && isDateValid(current) && formatDate(current, format, locale) === raw) {
            return true;
        }
        return isDateValid(parseDate(raw, format, locale, true));
    }

    private insertText(text: string): void {
        const input = this.elementRef.nativeElement;
        const start = input.selectionStart ?? input.value.length;
        const end = input.selectionEnd ?? input.value.length;
        const next = this.sanitize(input.value.slice(0, start) + text + input.value.slice(end));
        input.value = next;
        const caret = Math.min(start + text.length, next.length);
        input.setSelectionRange(caret, caret);
    }
}
